import React, { useState, useEffect, useCallback } from 'react';
import styles from './SaleStatusTab.module.css';
import PageComponent from 'components/common/Page/PageComponent';

import Api from 'utils/api';


const SaleStatusTab = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  const [machines, setMachines] = useState([]); // 드롭다운 목록
  const [selectedMachineId, setSelectedMachineId] = useState(''); // 선택된 machineId

  const [pageInfo, setPageInfo] = useState({
    pageNumber: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0
  });

  const fetchMachineList = useCallback(async () => {
    try {
      const data = await Api.get('/machine/simple'); // API 경로 확인 필요
      if (data && data.machineSimpleList) {
        setMachines(data.machineSimpleList);
      }
    } catch (err) {
      console.error("자판기 목록을 불러오는데 실패했습니다.", err);
    }
  }, []);

  // --- 2. 매출 내역 가져오기 (machineId 파라미터 추가) ---
  const fetchSaleHistory = useCallback(async (page, size, machineId) => {
    setLoading(true);
    try {
      // machineId가 있으면 쿼리에 추가, 없으면 제외
      let url = `/sale/history?pageNumber=${page}&pageSize=${size}`;
      if (machineId) {
        url += `&machineId=${machineId}`;
      }

      const data = await Api.get(url);
      const { content, totalElements, totalPages, number, size: resSize } = data.saleHistoryDetailList;

      setSales(content || []);
      setPageInfo({
        pageNumber: number,
        pageSize: resSize,
        totalElements: totalElements,
        totalPages: totalPages
      });
    } catch (err) {
      console.error("매출 내역을 불러오는데 실패했습니다.", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 초기 로드: 자판기 목록 가져오기
  useEffect(() => {
    fetchMachineList();
  }, [fetchMachineList]);

  // 페이지 번호나 선택된 자판기가 변경될 때마다 내역 재조회
  // 수정 후 (pageInfo 전체가 아닌 필요한 속성만 명시)
  const { pageNumber, pageSize } = pageInfo;
  useEffect(() => {
    fetchSaleHistory(pageNumber, pageSize, selectedMachineId);
  }, [fetchSaleHistory, pageNumber, pageSize, selectedMachineId]);

  // 자판기 변경 핸들러
  const handleMachineChange = (e) => {
    setSelectedMachineId(e.target.value);
    setPageInfo(prev => ({ ...prev, pageNumber: 0 })); // 필터 변경 시 1페이지로 리셋
  };

  const handlePageChange = (newPage) => {
    setPageInfo(prev => ({ ...prev, pageNumber: newPage }));
    window.scrollTo(0, 0);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }).replace(/\./g, '/');
  };

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>

        <div className={styles.countInfo}>
          전체 매출 <span className={styles.countNumber}>{pageInfo.totalElements}</span>건
          <span className={styles.pageLabel}> (페이지 {pageInfo.totalElements === 0 ? 0 : pageInfo.pageNumber + 1} / {pageInfo.totalPages})</span>
        </div>

        <div className={styles.filterSection}>
          <label className={styles.filterLabel} htmlFor="machine-select">필터</label>
          <select
            id="machine-select"
            className={styles.machineSelect}
            value={selectedMachineId}
            onChange={handleMachineChange}
          >
            <option value="">전체 자판기</option>
            {machines.map(m => (
              <option key={m.machineId} value={m.machineId}>
                {m.machineName}
              </option>
            ))}
          </select>
        </div>


      </div>

      <div className={styles.cardGrid}>
        {loading ? (
          <div className={styles.loading}>데이터 로딩 중...</div>
        ) : sales.length === 0 ? (
          <div className={styles.emptyMsg}>매출 내역이 없습니다.</div>
        ) : sales.map((item) => (
          <div key={item.saleHistoryId} className={styles.receiptCard}>

            {/* 상단: 일시 및 출고상태 */}
            <div className={styles.cardTop}>
              <span className={styles.dateTime}>{formatDateTime(item.saleDateTime)}</span>
              <span className={item.dispenseFlag === 1 ? styles.statusBlue : styles.statusRed}>
                {item.dispenseFlag === 1 ? '출고 완료' : '출고 실패'}
              </span>
            </div>

            {/* 중단: 이미지 및 상품 기본 정보 */}
            <div className={styles.productInfo}>
              <div className={styles.imgBox}>
                <img src={item.productImageUrl || '/default-img.png'} alt="" />
              </div>
              <div className={styles.prodMain}>
                <div className={styles.prodTitle}>{item.productName}</div>
              </div>
              <div className={styles.prodPriceBox}>
                <span className={styles.currentPrice}>{item.purchasePrice?.toLocaleString()}</span>
                <span className={styles.qty}>x {item.purchaseCount}</span>
              </div>
            </div>

            <div className={styles.divider}></div>

            {/* 하단: 상세 정보 표 */}
            <div className={styles.details}>
              <div className={styles.detailRow}>
                <span className={styles.label}>주문 금액 : <em className={styles.price}>{item.purchasePrice?.toLocaleString()}</em></span>
                <span className={styles.label}>실제 지불 금액 : <em className={styles.price}>{item.purchasePrice?.toLocaleString()}</em></span>
                <span className={styles.label}>결제 방법 : {item.paymentMethod}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>기기명 : {item.machineName}</span>
                <span className={styles.label}>기기 시리얼 번호 : {item.machineSerialNumber || '-'}</span>
                <span className={styles.label}>기기 칩 ID : {item.machineChipId || '-'}</span>


              </div>
              <div className={styles.detailRow}>
                <div className={styles.label}>주문 번호 : {item.orderNumber}</div>
                <div className={styles.label}>결제 번호 : {item.paymentNumber}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.pagination}>
        <PageComponent pageInfo={pageInfo} onPageChange={handlePageChange} />
      </div>
    </div>
  );
};

export default SaleStatusTab;